import React, { useContext, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { domain } from './Domain.jsx';
import { AuthContext } from './AuthContext.jsx';
import useApiRequest from './useApiRequest.jsx';
import { motion, AnimatePresence } from 'framer-motion';
import notify from './Nots.jsx';
import withAuthCheck from './WithAuthCheck.jsx';
import {
    User, Mail, Lock, Phone, Eye, EyeOff, ArrowLeft, ArrowRight,
    CheckCircle, AlertCircle, Loader2, Shield, Users, Calendar
} from 'lucide-react';

const Register = () => {
    const [success, setSuccess] = useState(false);
    const { postRequest, loading } = useApiRequest();
    const { authToken, login, logout } = useContext(AuthContext);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const initial = {
        // username: '',
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        confirmPassword: '',
        contact: '',
        profile: null
    };

    const [formData, setFormData] = useState(initial);
    const [isPasswordMatch, setIsPasswordMatch] = useState(true);
    const [formErrors, setFormErrors] = useState({});
    const navigate = useNavigate();

    useEffect(() => {
        const errors = validateForm(formData);
        setFormErrors(errors);
        setIsPasswordMatch(formData.password === formData.confirmPassword);
    }, [formData]);

    const validateForm = (data) => {
        let errors = {};

        if (!data.first_name.trim()) {
            errors.first_name = 'First name is required';
        } else if (data.first_name.length < 2) {
            errors.first_name = 'First name must be at least 2 characters';
        }

        if (!data.last_name.trim()) {
            errors.last_name = 'Last name is required';
        } else if (data.last_name.length < 2) {
            errors.last_name = 'Last name must be at least 2 characters';
        }

        /*if (!data.username.trim()) {
            errors.username = 'Username is required';
        } else if (data.username.length < 3) {
            errors.username = 'Username must be at least 3 characters';
        }*/

        if (!data.contact.trim()) {
            errors.contact = 'Phone number is required';
        } else if (data.contact.length < 10) {
            errors.contact = 'Please enter a valid phone number';
        }

        if (!data.email.trim()) {
            errors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(data.email)) {
            errors.email = 'Please enter a valid email address';
        }

        if (!data.password.trim()) {
            errors.password = 'Password is required';
        } else if (data.password.length < 6) {
            errors.password = 'Password must be at least 6 characters';
        }

        if (!data.confirmPassword.trim()) {
            errors.confirmPassword = 'Please confirm your password';
        } else if (data.password !== data.confirmPassword) {
            errors.confirmPassword = 'Passwords do not match';
        }

        return errors;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleRegister = async (e) => {
        e.preventDefault();

        formData['username'] = formData['email']

        const validationErrors = validateForm(formData);
        setErrors(validationErrors);

        if (Object.keys(validationErrors).length === 0) {
            setIsSubmitting(true);

            try {
                const formDatas = new FormData();
                for (const key in formData) {
                    formDatas.append(key, formData[key]);
                }

                const response = await postRequest(`${domain}/accounts/signup`, formDatas, true);
                console.log("Registration response:", response);
                
                // Handle successful responses
                if (response.success && (response.status === 200 || response.status === 201)) {
                    if (response.body && response.body.status === "success") {
                        notify(response.body.message || "Registration successful! Please check your email for activation.", "info");
                        setSuccess(true);
                        setFormData(initial);
                        e.target.reset();
                    }
                } 
                // Handle error responses (400, 403, etc.)
                else if (response.status === 400 || response.status === 403) {
                    // Get error data from response body
                    const errorData = response.body || response.details || {};
                    const errorMessage = errorData.message || errorData.errors || errorData;
                    
                    // Set field-specific errors for display
                    const fieldErrors = {};
                    
                    if (typeof errorMessage === 'object' && !Array.isArray(errorMessage)) {
                        // Handle field-specific validation errors
                        Object.entries(errorMessage).forEach(([field, errors]) => {
                            const errorArray = Array.isArray(errors) ? errors : [errors];
                            // Store first error for each field for inline display
                            fieldErrors[field] = errorArray[0];
                            
                            // Show notification for each error
                            errorArray.forEach(err => {
                                const errorText = typeof err === 'string' ? err : JSON.stringify(err);
                                const fieldName = field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                                notify(`${fieldName}: ${errorText}`, "error");
                            });
                        });
                        setErrors(fieldErrors);
                    } else {
                        // Handle non-field-specific error messages
                        const errorText = typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage);
                        notify(errorText, "error");
                    }
                } 
                // Handle other error statuses
                else {
                    const errorMessage = response.body?.message || response.message || "An error occurred during registration";
                    if (typeof errorMessage === 'object' && !Array.isArray(errorMessage)) {
                        Object.entries(errorMessage).forEach(([field, errors]) => {
                            const errorArray = Array.isArray(errors) ? errors : [errors];
                            errorArray.forEach(err => {
                                const fieldName = field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                                notify(`${fieldName}: ${err}`, "error");
                            });
                        });
                    } else {
                        notify(errorMessage, "error");
                    }
                }

            } catch (error) {
                notify(`Request failed: ${error.message}`, "error");
            } finally {
                setIsSubmitting(false);
            }
        } else {
            for (const key in validationErrors) {
                notify(validationErrors[key], "error");
            }
        }
    };

    const features = [
        { icon: <Shield className="w-5 h-5" />, text: "Secure account creation" },
        { icon: <Users className="w-5 h-5" />, text: "Connect with event organizers" },
        { icon: <Calendar className="w-5 h-5" />, text: "Manage your events" }
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-orange-50 via-red-50 to-pink-50">
            <div className="container mx-auto px-4 py-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center min-h-[80vh]">
                    {/* Left Side - Register Form */}
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6 }}
                        className="flex justify-center"
                    >
                        <div className="w-full max-w-lg">
                            {/* Back to Login */}
                            <motion.div
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.2 }}
                                className="mb-6"
                            >
                                <Link
                                    to="/login"
                                    className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors duration-200 group"
                                >
                                    <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                                    <span className="text-sm font-medium">Back to Login</span>
                                </Link>
                            </motion.div>

                            {/* Register Form */}
                            <AnimatePresence>
                                {!success ? (
                                    <motion.div
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -20 }}
                                        transition={{ duration: 0.6, delay: 0.3 }}
                                        className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8"
                                    >
                                        {/* Header */}
                                        <div className="text-center mb-8">
                                            <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                                                <User className="w-8 h-8 text-white" />
                                            </div>
                                            <h1 className="text-lg font-bold text-gray-900 mb-2">Create Account</h1>
                                            <p className="text-sm text-gray-600">Join EventPlots to discover amazing events</p>
                                        </div>

                                        {/* Form */}
                                        <form onSubmit={handleRegister} className="space-y-6">
                                            {/* Full Name */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Full Name
                                                </label>
                                                <div className="grid grid-cols-2 gap-3">
                                                    <div className="relative">
                                                        <input
                                                            type="text"
                                                            name="first_name"
                                                            value={formData.first_name}
                                                            onChange={handleChange}
                                                            className={`w-full pl-3 pr-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 ${errors.first_name ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-gray-400'
                                                                }`}
                                                            placeholder="First name"
                                                        />
                                                    </div>
                                                    <div className="relative">
                                                        <input
                                                            type="text"
                                                            name="last_name"
                                                            value={formData.last_name}
                                                            onChange={handleChange}
                                                            className={`w-full pl-3 pr-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 ${errors.last_name ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-gray-400'
                                                                }`}
                                                            placeholder="Last name"
                                                        />
                                                    </div>
                                                </div>
                                                <AnimatePresence>
                                                    {(errors.first_name || errors.last_name) && (
                                                        <motion.div
                                                            initial={{ opacity: 0, y: -10 }}
                                                            animate={{ opacity: 1, y: 0 }}
                                                            exit={{ opacity: 0, y: -10 }}
                                                            className="flex items-center gap-1 mt-1 text-red-600 text-xs"
                                                        >
                                                            <AlertCircle className="w-3 h-3" />
                                                            {errors.first_name || errors.last_name}
                                                        </motion.div>
                                                    )}
                                                </AnimatePresence>
                                            </div>

                                            {/* Username */}
                                            <div className='hidden'>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Username
                                                </label>
                                                <div className="relative">
                                                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                                    <input
                                                        type="text"
                                                        name="username"
                                                        value={formData.username}
                                                        onChange={handleChange}
                                                        className={`w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 ${errors.username ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-gray-400'
                                                            }`}
                                                        placeholder="Choose a username"
                                                    />
                                                </div>
                                                <AnimatePresence>
                                                    {errors.username && (
                                                        <motion.div
                                                            initial={{ opacity: 0, y: -10 }}
                                                            animate={{ opacity: 1, y: 0 }}
                                                            exit={{ opacity: 0, y: -10 }}
                                                            className="flex items-center gap-1 mt-1 text-red-600 text-sm"
                                                        >
                                                            <AlertCircle className="w-4 h-4" />
                                                            {errors.username}
                                                        </motion.div>
                                                    )}
                                                </AnimatePresence>
                                            </div>

                                            {/* Phone Number */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Phone Number
                                                </label>
                                                <div className="relative">
                                                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                                    <input
                                                        type="tel"
                                                        name="contact"
                                                        value={formData.contact}
                                                        onChange={handleChange}
                                                        className={`w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 ${errors.contact ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-gray-400'
                                                            }`}
                                                        placeholder="Enter your phone number"
                                                    />
                                                </div>
                                                <AnimatePresence>
                                                    {errors.contact && (
                                                        <motion.div
                                                            initial={{ opacity: 0, y: -10 }}
                                                            animate={{ opacity: 1, y: 0 }}
                                                            exit={{ opacity: 0, y: -10 }}
                                                            className="flex items-center gap-1 mt-1 text-red-600 text-sm"
                                                        >
                                                            <AlertCircle className="w-4 h-4" />
                                                            {errors.contact}
                                                        </motion.div>
                                                    )}
                                                </AnimatePresence>
                                            </div>

                                            {/* Email */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Email 
                                                </label>
                                                <div className="relative">
                                                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                                    <input
                                                        type="email"
                                                        name="email"
                                                        value={formData.email}
                                                        onChange={handleChange}
                                                        className={`w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 ${errors.email ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-gray-400'
                                                            }`}
                                                        placeholder="Enter your email"
                                                    />
                                                </div>
                                                <small className='text-gray-500'>used as ur username as well</small>
                                                <AnimatePresence>
                                                    {errors.email && (
                                                        <motion.div
                                                            initial={{ opacity: 0, y: -10 }}
                                                            animate={{ opacity: 1, y: 0 }}
                                                            exit={{ opacity: 0, y: -10 }}
                                                            className="flex items-center gap-1 mt-1 text-red-600 text-sm"
                                                        >
                                                            <AlertCircle className="w-4 h-4" />
                                                            {errors.email}
                                                        </motion.div>
                                                    )}
                                                </AnimatePresence>
                                            </div>

                                            {/* Password */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Password
                                                </label>
                                                <div className="grid grid-cols-2 gap-3">
                                                    <div className="relative">
                                                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                                        <input
                                                            type={showPassword ? "text" : "password"}
                                                            name="password"
                                                            value={formData.password}
                                                            onChange={handleChange}
                                                            className={`w-full pl-9 pr-9 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 ${errors.password ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-gray-400'
                                                                }`}
                                                            placeholder="Password"
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={() => setShowPassword(!showPassword)}
                                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                                                        >
                                                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                                        </button>
                                                    </div>
                                                    <div className="relative">
                                                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                                        <input
                                                            type={showConfirmPassword ? "text" : "password"}
                                                            name="confirmPassword"
                                                            value={formData.confirmPassword}
                                                            onChange={handleChange}
                                                            className={`w-full pl-9 pr-9 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 ${errors.confirmPassword ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-gray-400'
                                                                }`}
                                                            placeholder="Confirm password"
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                                                        >
                                                            {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                                        </button>
                                                    </div>
                                                </div>
                                                <AnimatePresence>
                                                    {(errors.password || errors.confirmPassword || !isPasswordMatch) && (
                                                        <motion.div
                                                            initial={{ opacity: 0, y: -10 }}
                                                            animate={{ opacity: 1, y: 0 }}
                                                            exit={{ opacity: 0, y: -10 }}
                                                            className="flex items-center gap-1 mt-1 text-red-600 text-sm"
                                                        >
                                                            <AlertCircle className="w-4 h-4" />
                                                            {errors.password || errors.confirmPassword || (!isPasswordMatch && 'Passwords do not match')}
                                                        </motion.div>
                                                    )}
                                                </AnimatePresence>
                                            </div>

                                            {/* Submit Button */}
                                            <button
                                                type="submit"
                                                disabled={isSubmitting || Object.keys(formErrors).length > 0}
                                                className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white! py-3 px-4 rounded-lg font-medium transition-all duration-200 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                            >
                                                {isSubmitting ? (
                                                    <>
                                                        <Loader2 className="w-5 h-5 animate-spin" />
                                                        Creating Account...
                                                    </>
                                                ) : (
                                                    <>
                                                        Create Account
                                                        <ArrowRight className="w-5 h-5" />
                                                    </>
                                                )}
                                            </button>
                                        </form>

                                        {/* Sign In Link */}
                                        <div className="mt-6 text-center">
                                            <p className="text-gray-600 text-sm">
                                                Already have an account?{' '}
                                                <Link
                                                    to="/login"
                                                    className="text-orange-600 hover:text-orange-700 font-medium transition-colors duration-200"
                                                >
                                                    Sign in here
                                                </Link>
                                            </p>
                                        </div>
                                    </motion.div>
                                ) : (
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.8 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.8 }}
                                        transition={{ duration: 0.5 }}
                                        className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 text-center"
                                    >
                                        <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                                            <CheckCircle className="w-8 h-8 text-white" />
                                        </div>
                                        <h1 className="text-lg font-bold text-gray-900 mb-2">Registration Successful!</h1>
                                        <p className="text-sm text-gray-600 mb-6">Thank you for registering. You can now log in to your account.</p>
                                        <Link
                                            to="/login"
                                            className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white px-6 py-3 rounded-lg font-medium transition-all duration-200 hover:shadow-lg"
                                        >
                                            Go to Login
                                            <ArrowRight className="w-4 h-4" />
                                        </Link>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </motion.div>

                    {/* Right Side - Features & Info */}
                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6, delay: 0.4 }}
                        className="hidden lg:block"
                    >
                        <div className="max-w-lg">
                            <div className="mb-8">
                                <h2 className="text-lg font-bold text-gray-900 mb-4">
                                    Join EventPlots Today
                                </h2>
                                <p className="text-sm text-gray-600 leading-relaxed">
                                    Create your account and start discovering amazing events.
                                    Connect with organizers, book tickets, and never miss out on the best experiences.
                                </p>
                            </div>

                            <div className="space-y-4">
                                {features.map((feature, index) => (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, x: 20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ duration: 0.4, delay: 0.6 + index * 0.1 }}
                                        className="flex items-center gap-3"
                                    >
                                        <div className="w-10 h-10 bg-gradient-to-r from-orange-100 to-red-100 rounded-lg flex items-center justify-center">
                                            <div className="text-orange-600">
                                                {feature.icon}
                                            </div>
                                        </div>
                                        <span className="text-sm text-gray-700 font-medium">{feature.text}</span>
                                    </motion.div>
                                ))}
                            </div>

                            {/* Trust Indicators */}
                            <div className="mt-8 p-6 bg-white/50 backdrop-blur-sm rounded-xl border border-white/20">
                                <div className="flex items-center gap-2 mb-3">
                                    <Shield className="w-5 h-5 text-orange-600" />
                                    <span className="text-sm font-semibold text-gray-900">Secure Registration</span>
                                </div>
                                <p className="text-sm text-gray-600">
                                    Your information is protected with enterprise-grade security. Join thousands of users
                                    who trust EventPlots for their event needs.
                                </p>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </div>
    );
};

export default withAuthCheck(Register);